"use client"

import { CheckCircle2, Clock, Hash, Layers, Users2 } from "lucide-react"

import { Button } from "@/common/components/ui/button"
import { BAUSTEIN_BY_ID } from "@/modules/lernpfad/data/bausteine"
import { moodleCmid } from "@/modules/lernpfad/data/connectors"
import { SKILL_BY_ID } from "@/modules/lernpfad/data/skills"
import type { LernpfadDemoState } from "@/app/demo/lernpfad/use-lernpfad-demo"

/** Stable 32-bit FNV-1a — same implementation as every other file in this module. */
function fnv1a(input: string): number {
    let hash = 0x811c9dc5
    for (let index = 0; index < input.length; index++) {
        hash ^= input.charCodeAt(index)
        hash = Math.imul(hash, 0x01000193)
    }
    return hash >>> 0
}

/** Deterministic, plausible-looking enrolment count — never `Math.random()`. */
function enrolmentCount(bausteinId: string): number {
    return 180 + (fnv1a(`enrol:${bausteinId}`) % 950)
}

export default function LmsCourseDetail({
    state,
    bausteinId,
    onStart,
}: {
    state: LernpfadDemoState
    bausteinId: string
    onStart: (bausteinId: string) => void
}) {
    const { localizer, employee } = state
    const { t } = localizer
    const baustein = BAUSTEIN_BY_ID.get(bausteinId)
    if (!baustein) return null

    const content = localizer.baustein(baustein)
    const alreadyDone = employee.completed.some((c) => c.bausteinId === bausteinId)
    const prerequisites = baustein.prerequisites
        .map((id) => BAUSTEIN_BY_ID.get(id))
        .filter((b) => b !== undefined)
    const isOnline = baustein.format === "e-learning"

    return (
        <div className="space-y-5 p-4 pt-3">
            <div>
                <h2 className="text-xl font-semibold">{content.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {content.description}
                </p>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Field icon={Clock} label={t("lms.detail.durationLabel")}>
                    {t("item.hours", { hours: baustein.durationHours })}
                </Field>
                <Field icon={Layers} label={t("lms.detail.formatLabel")}>
                    {localizer.format(baustein.format)}
                </Field>
                <Field icon={Users2} label={t("lms.detail.providerLabel")}>
                    {content.provider}
                </Field>
                <Field icon={Hash} label={t("lms.detail.levelLabel")}>
                    {localizer.level(baustein.level)}
                </Field>
            </div>

            <div>
                <p className="mb-2 text-xs font-medium text-muted-foreground">
                    {t("lms.detail.skillsLabel")}
                </p>
                <div className="flex flex-wrap gap-1.5">
                    {baustein.teaches.map((taught) => (
                        <span
                            key={taught.skillId}
                            className="inline-flex items-center gap-1 rounded-md border bg-muted/30 px-2 py-1 text-xs"
                        >
                            {localizer.skill(taught.skillId, SKILL_BY_ID.get(taught.skillId)?.label ?? taught.skillId)}
                            <span className="text-muted-foreground">· {localizer.level(taught.level)}</span>
                        </span>
                    ))}
                </div>
            </div>

            {prerequisites.length > 0 && (
                <div>
                    <p className="mb-2 text-xs font-medium text-muted-foreground">
                        {t("lms.detail.prerequisitesLabel")}
                    </p>
                    <ul className="space-y-1 text-sm">
                        {prerequisites.map((prereq) => (
                            <li key={prereq!.id} className="text-muted-foreground">
                                {localizer.baustein(prereq!).title}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="flex items-center justify-between border-t pt-4 text-xs text-muted-foreground">
                <span>{t("lms.detail.enrolled", { count: enrolmentCount(bausteinId) })}</span>
                <span className="tabular-nums">
                    {t("lms.detail.courseId", { id: moodleCmid(bausteinId) })}
                </span>
            </div>

            <div className="flex justify-end">
                {alreadyDone ? (
                    <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                        <CheckCircle2 className="size-4 text-emerald-600 dark:text-emerald-400" />
                        {t("lms.detail.alreadyDone")}
                    </span>
                ) : isOnline ? (
                    <Button onClick={() => onStart(bausteinId)}>{t("lms.detail.start")}</Button>
                ) : (
                    <p className="text-sm text-muted-foreground">
                        {t("lms.detail.notOnline", { provider: content.provider })}
                    </p>
                )}
            </div>
        </div>
    )
}

function Field({
    icon: Icon,
    label,
    children,
}: {
    icon: typeof Clock
    label: string
    children: React.ReactNode
}) {
    return (
        <div className="rounded-lg border bg-muted/20 p-2.5">
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
                <Icon className="size-3" />
                {label}
            </p>
            <p className="mt-0.5 truncate text-sm font-medium">{children}</p>
        </div>
    )
}
